import { c as _c } from "react/compiler-runtime";
import { Box, Stack, Typography } from '@mui/material';
import { StyledCheckbox } from '@/components/form/baseInputs/Checkbox/Checkbox';
import { DraggableListItemInnerComponentProps } from '@/components/lists/DragAndDropList/DraggableListItem.types';
import { Draghandle } from '@/components/lists/DragAndDropList/Draghandle';
import { COLORS } from '@/styles/tokens/colors';
import { BalanceSheetConfigColumn } from './BalanceSheetConfigPanel.types';
interface ColumnRowContentProps {
  item: BalanceSheetConfigColumn;
  draghandle: JSX.Element;
  isStatic?: boolean;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  /** Whether this column is below the overflow cutoff line (asterisk on name) */
  isOverflow?: boolean;
  /** Whether this is the last item above the cutoff line (renders darker bottom border) */
  isCutoffItem?: boolean;
  /** Whether overflow mode is active — when true, this component renders its own borders
   *  (since DragAndDropList withDividers is disabled). */
  hasOverflow?: boolean;
}
function ColumnRowContent(t0) {
  const $ = _c(26);
  const {
    item,
    draghandle,
    isStatic,
    checked,
    onCheckedChange,
    isOverflow,
    isCutoffItem,
    hasOverflow
  } = t0;
  const displayName = isOverflow ? `${item.display}*` : item.display;
  const borderBottom = hasOverflow ? isCutoffItem ? `1px solid ${COLORS.NAVY[500]}` : `1px solid ${COLORS.GRAY[200]}` : undefined;
  let t1;
  if ($[0] !== borderBottom) {
    t1 = {
      borderBottom
    };
    $[0] = borderBottom;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = isStatic ? "default" : "grab";
  let t3;
  if ($[2] !== t2) {
    t3 = {
      cursor: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== draghandle || $[5] !== t3) {
    t4 = <Box sx={t3}>{draghandle}</Box>;
    $[4] = draghandle;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== displayName) {
    t5 = <Typography variant="body-xs" color={COLORS.TEXT.PRIMARY}>{displayName}</Typography>;
    $[7] = displayName;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== item.groupDisplay) {
    t6 = <Typography variant="body-xs">{item.groupDisplay}</Typography>;
    $[9] = item.groupDisplay;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <Stack gap={0.5}>{t5}{t6}</Stack>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t4 || $[15] !== t7) {
    t8 = <Stack direction="row" alignItems="center" gap={1}>{t4}{t7}</Stack>;
    $[14] = t4;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== checked || $[18] !== isStatic || $[19] !== item.id || $[20] !== onCheckedChange) {
    t9 = checked !== undefined && <StyledCheckbox value={checked} disabled={isStatic} onChange={e => onCheckedChange?.(e.target.checked)} name={`visibility-${item.id}`} />;
    $[17] = checked;
    $[18] = isStatic;
    $[19] = item.id;
    $[20] = onCheckedChange;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t1 || $[23] !== t8 || $[24] !== t9) {
    t10 = <Stack direction="row" alignItems="center" justifyContent="space-between" gap={0.5} py={1} px={0.5} sx={t1}>{t8}{t9}</Stack>;
    $[22] = t1;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
}
export type BalanceSheetConfigColumnItemProps = DraggableListItemInnerComponentProps<BalanceSheetConfigColumn> & {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  isOverflow?: boolean;
  isCutoffItem?: boolean;
  hasOverflow?: boolean;
};

/** Draggable column row used inside the DragAndDropList. */
export function BalanceSheetConfigColumnItem(t0) {
  const $ = _c(8);
  const {
    item,
    Draghandle: DraghandleFromList,
    checked,
    onCheckedChange,
    isOverflow,
    isCutoffItem,
    hasOverflow
  } = t0;
  let t1;
  if ($[0] !== DraghandleFromList || $[1] !== checked || $[2] !== hasOverflow || $[3] !== isCutoffItem || $[4] !== isOverflow || $[5] !== item || $[6] !== onCheckedChange) {
    t1 = <ColumnRowContent item={item} draghandle={DraghandleFromList} checked={checked} onCheckedChange={onCheckedChange} isOverflow={isOverflow} isCutoffItem={isCutoffItem} hasOverflow={hasOverflow} />;
    $[0] = DraghandleFromList;
    $[1] = checked;
    $[2] = hasOverflow;
    $[3] = isCutoffItem;
    $[4] = isOverflow;
    $[5] = item;
    $[6] = onCheckedChange;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
interface StaticColumnRowProps {
  item: BalanceSheetConfigColumn;
  showCheckbox?: boolean;
}

/** Non-draggable row for total columns — drag handle is shown but faded. */
export function StaticColumnRow(t0) {
  const $ = _c(4);
  const {
    item,
    showCheckbox
  } = t0;
  const t1 = showCheckbox ? true : undefined;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Draghandle iconProps={{
      sx: {
        opacity: 0.24
      }
    }} />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== item || $[2] !== t1) {
    t3 = <ColumnRowContent item={item} isStatic={true} checked={t1} draghandle={t2} />;
    $[1] = item;
    $[2] = t1;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}